import {
  createContext,
  useContext,
  useCallback,
  useRef,
  useMemo,
  type ReactNode,
} from 'react';
import {
  useWebSocket,
  type WebSocketEvent,
  type ConnectionState,
  EventType,
} from '../hooks/useWebSocket';

/**
 * Event handler function type
 */
export type EventHandler = (event: WebSocketEvent) => void;

/**
 * WebSocket context value interface
 */
export interface WebSocketContextValue {
  /** Whether the WebSocket is currently connected */
  isConnected: boolean;
  /** Current connection state */
  connectionState: ConnectionState;
  /** Subscribe to a specific event type */
  subscribe: (eventType: EventType | string, handler: EventHandler) => () => void;
  /** Send a message through the WebSocket */
  sendMessage: (message: unknown) => void;
  /** Manually reconnect */
  reconnect: () => void;
  /** Close the connection */
  disconnect: () => void;
}

// Create context with undefined default
const WebSocketContext = createContext<WebSocketContextValue | undefined>(undefined);

/**
 * Props for the WebSocketProvider component
 */
interface WebSocketProviderProps {
  children: ReactNode;
}

/**
 * Provider component that establishes and manages the WebSocket connection
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <WebSocketProvider>
 *       <MyApp />
 *     </WebSocketProvider>
 *   );
 * }
 * ```
 */
export function WebSocketProvider({ children }: WebSocketProviderProps) {
  // Store event handlers by event type
  const handlersRef = useRef<Map<string, Set<EventHandler>>>(new Map());

  /**
   * Handle incoming WebSocket messages and dispatch to subscribers
   */
  const handleMessage = useCallback((event: WebSocketEvent) => {
    const handlers = handlersRef.current.get(event.type);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(event);
        } catch (error) {
          console.error(`WebSocket: Error in event handler for ${event.type}:`, error);
        }
      });
    }

    // Also dispatch to wildcard subscribers (*)
    const wildcardHandlers = handlersRef.current.get('*');
    if (wildcardHandlers) {
      wildcardHandlers.forEach((handler) => {
        try {
          handler(event);
        } catch (error) {
          console.error('WebSocket: Error in wildcard event handler:', error);
        }
      });
    }
  }, []);

  // Initialize WebSocket connection
  const {
    isConnected,
    connectionState,
    sendMessage,
    reconnect,
    disconnect,
  } = useWebSocket({
    onMessage: handleMessage,
    onConnect: () => {
      console.log('WebSocket: Connected to server');
    },
    onDisconnect: () => {
      console.log('WebSocket: Disconnected from server');
    },
    onError: (error) => {
      console.error('WebSocket: Connection error:', error);
    },
  });

  /**
   * Subscribe to a specific event type
   *
   * @param eventType - The event type to subscribe to (or '*' for all events)
   * @param handler - The handler function to call when the event is received
   * @returns Unsubscribe function
   */
  const subscribe = useCallback(
    (eventType: EventType | string, handler: EventHandler): (() => void) => {
      // Get or create handler set for this event type
      if (!handlersRef.current.has(eventType)) {
        handlersRef.current.set(eventType, new Set());
      }
      const handlers = handlersRef.current.get(eventType)!;
      handlers.add(handler);

      // Return unsubscribe function
      return () => {
        handlers.delete(handler);
        if (handlers.size === 0) {
          handlersRef.current.delete(eventType);
        }
      };
    },
    []
  );

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<WebSocketContextValue>(
    () => ({
      isConnected,
      connectionState,
      subscribe,
      sendMessage,
      reconnect,
      disconnect,
    }),
    [isConnected, connectionState, subscribe, sendMessage, reconnect, disconnect]
  );

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Hook to access the WebSocket context
 *
 * @returns WebSocket context value
 * @throws Error if used outside of WebSocketProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isConnected, subscribe } = useWebSocketContext();
 *
 *   useEffect(() => {
 *     const unsubscribe = subscribe('agent.created', (event) => {
 *       console.log('New agent created:', event.payload);
 *     });
 *     return unsubscribe;
 *   }, [subscribe]);
 *
 *   return <div>Connected: {isConnected ? 'Yes' : 'No'}</div>;
 * }
 * ```
 */
export function useWebSocketContext(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
}

// Re-export EventType for convenience
export { EventType };

export default WebSocketProvider;
